// A sign-out reaches every tab (36-desyr-id.md, Behaviour 4.4, v1.58.63): the signing-out tab moves the
// sign-in generation on before anything else and tells the others on the BroadcastChannel `desyr_auth`;
// the others also watch the `storage` event and compare when they become visible, in case a message
// was missed. A tab that learns of it stops at once and shows the signed-out card, no jump.
import { isSigningOut } from '../core/session';
import { hopUsed } from './hop';
import { authGen, forgetUnderWay, GEN, markSignedOut } from './store';

const CHANNEL = 'desyr_auth';
/** This tab's generation: the browser's when it started, or its own sign-out's. */
let mine = authGen();
let out = false;
const hooks = new Set<() => void>();

/** Another tab signed out: this one shows nothing of the account any more. */
export const outByOtherTab = () => out;

/** The start of every desyr ID sign-out (4.4.1): the generation on, everything under way forgotten, the other tabs told. */
export function signOutTabs(): void {
  const gen = authGen() + 1;
  try {
    localStorage.setItem(GEN, String(gen));
  } catch {
    // storage refused: the message still goes
  }
  mine = gen;
  forgetUnderWay();
  hopUsed();
  markSignedOut(); // until the end-session's state is known (4.3); a return meanwhile finds it
  try {
    const channel = new BroadcastChannel(CHANNEL);
    channel.postMessage({ type: 'signed-out', gen });
    channel.close();
  } catch {
    // no BroadcastChannel: the storage event tells the others
  }
}

let watching = false;
function check(gen = authGen()): void {
  // The tab signing out itself goes on with its own steps.
  if (gen <= mine || isSigningOut()) return;
  mine = gen;
  out = true;
  hooks.forEach((hook) => hook());
}

/** `hook` runs once another tab has signed out (4.4.2). */
export function followSignOut(hook: () => void): () => void {
  hooks.add(hook);
  if (!watching) {
    watching = true;
    try {
      new BroadcastChannel(CHANNEL).onmessage = (e: MessageEvent<{ type?: string; gen?: number }>) => e.data?.type === 'signed-out' && check(Math.max(Number(e.data.gen) || 0, authGen()));
    } catch {
      // no BroadcastChannel: storage and visibility below
    }
    addEventListener('storage', (e) => e.key === GEN && check());
    document.addEventListener('visibilitychange', () => document.visibilityState === 'visible' && check());
  }
  return () => void hooks.delete(hook);
}
